<script setup lang="ts">
/**
 * El presupuesto, como se ve — y donde se decide qué hacer con él (§47).
 *
 * Es el preview del recibo con tres acciones más, y las tres son de este
 * documento: enviarlo, registrar lo que contestó el cliente, y convertirlo en
 * reservas. El PDF sigue siendo el único que emite el papel; esto lo muestra.
 */
import { apiOrigin } from '@/api/client'
import { getClient } from '@/api/clients'
import { getDiveCenter } from '@/api/dive-centers'
import {
  type ConversionLine,
  type ConversionOptions,
  type Quote,
  type QuoteBooking,
  type QuoteLine,
  convertQuote,
  createQuotePdfLink,
  decideQuote,
  getConversionOptions,
  getQuote,
  issueQuote,
  sendQuote,
} from '@/api/quotes'
import AlertBox from '@/components/AlertBox.vue'
import { serviceColor } from '@/data/serviceColors'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess, notifyWarning } from '@/composables/useSnackbar'
import type { DiveCenter } from '@/types'

definePage({
  meta: {
    requiresAuth: true,
    navActiveLink: 'sales-quotes',
  },
})

const route = useRoute('sales-quotes-quote-id-preview')
const quoteId = String(route.params.quoteId)

const { currentId } = useCurrentDiveCenter()
const { formatCurrency, formatDate } = usePreferences()

const quote = ref<Quote | null>(null)

/**
 * Contra qué salidas quedó reservado.
 *
 * El ícono de la línea dice QUE se convirtió; esto dice CONTRA QUÉ, que es otra
 * pregunta: la salida elegida bien puede ser otro día del que se pidió.
 */
const bookings = ref<QuoteBooking[]>([])
const diveCenter = ref<DiveCenter | null>(null)
const loading = ref(true)
const error = ref('')
const pdfLoading = ref(false)

const STATUS_CHIP = {
  draft: { label: 'Draft', color: 'secondary' },
  sent: { label: 'Sent', color: 'info' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  expired: { label: 'Expired', color: 'warning' },

  // Morado y no verde: en el listado se confundía con Approved.
  converted: { label: 'Converted', color: '#7E57C2' },
  partially_converted: { label: 'Partly converted', color: 'warning' },
} as const

const issuerAddress = computed(() => {
  const a = diveCenter.value?.address

  if (!a)
    return []

  return [
    a.formattedAddress ?? [a.street, a.city].filter(Boolean).join(', '),
    [a.postalCode, a.country].filter(Boolean).join(' '),
  ].filter(Boolean)
})

const issuerContact = computed(() =>
  [diveCenter.value?.contactPhone, diveCenter.value?.contactEmail].filter(Boolean),
)

const currency = computed(() => quote.value?.currency ?? 'EUR')
const money = (amount: number) => formatCurrency(amount, currency.value)

/** El mail de la ficha, para precargar el envío. */
const clientEmail = ref('')

async function load() {
  const dcId = currentId.value

  if (!dcId)
    return

  loading.value = true
  error.value = ''
  try {
    const [res, dcRes] = await Promise.all([getQuote(dcId, quoteId), getDiveCenter(dcId)])

    quote.value = res.quote
    bookings.value = res.bookings ?? []
    diveCenter.value = dcRes.diveCenter

    // Best-effort: sin esto el panel de envío abre con el campo vacío, que es
    // exactamente lo que hay que hacer si el recibo perdió su ficha.
    if (res.quote.clientId) {
      const client = await getClient(res.quote.clientId).catch(() => null)

      clientEmail.value = client?.client.email ?? ''
    }
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load this quote.')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * 🪤 **Y al cambiar el centro, que en un refresh llega DESPUÉS.**
 *
 * `currentId` se resuelve de forma asíncrona al arrancar la sesión. Entrando por
 * el listado ya está puesto, pero recargando esta URL directamente `onMounted`
 * corre con el centro todavía sin resolver: `load` se iba por el `return` de
 * arriba y la pantalla quedaba con la barra de carga puesta para siempre. Con el
 * watch, la primera resolución dispara la carga real.
 */
watch(currentId, load)

/**
 * 🪤 La pestaña se abre **antes** del `await`: un `window.open` posterior a una
 * espera ya no cuenta como gesto del usuario y lo mata el bloqueador (§21.7).
 */
async function openPdf() {
  const dcId = currentId.value

  if (!dcId)
    return

  const tab = window.open('', '_blank')

  pdfLoading.value = true
  try {
    const { url } = await createQuotePdfLink(dcId, quoteId)
    const absolute = new URL(url, apiOrigin()).toString()

    if (tab)
      tab.location.href = absolute
    else
      window.open(absolute, '_blank')
  }
  catch (e) {
    tab?.close()
    notifyError(apiMessage(e, 'Could not open the quote.'))
  }
  finally {
    pdfLoading.value = false
  }
}

// ---- Envío ----

const sendOpen = ref(false)
const sending = ref(false)
const sendTo = ref('')
const sendSubject = ref('')
const sendMessage = ref('')
const sendFormRef = ref()

const emailRule = (v: string) =>
  (!!v && /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(v)) || 'Enter a valid email address.'

const requiredRule = (v: string) => (!!v && !!v.trim()) || 'Required.'

/*
 * Asunto y mensaje se recargan cada vez que se abre: con un borrador viejo en
 * memoria, un reenvío después de corregir diría el número equivocado. Y el
 * saludo va DENTRO del mensaje, no en el template: todo lo que sale en el cuerpo
 * tiene que estar en el textarea, o quien envía lee una cosa y el cliente recibe
 * otra (§48.5).
 */
function openSend() {
  const center = diveCenter.value?.name ?? 'the dive centre'

  sendTo.value = clientEmail.value
  sendSubject.value = `Your quote from ${center}`
  sendMessage.value
    = `Hi ${quote.value?.clientName ?? ''},\n\n`
    + `Thank you for your interest in diving with ${center}. `
    + 'Please find your quote attached.\n\n'
    + 'Let us know if you would like to go ahead and we will confirm availability.'

  sendOpen.value = true
}

async function submitSend() {
  const dcId = currentId.value

  if (!dcId || !quote.value)
    return

  const { valid } = await sendFormRef.value.validate()

  if (!valid)
    return

  sending.value = true
  try {
    const { quote: saved } = await sendQuote(dcId, quote.value.id, {
      to: sendTo.value.trim(),
      subject: sendSubject.value.trim(),
      message: sendMessage.value.trim(),
    })

    quote.value = saved
    clientEmail.value = saved.sentTo ?? sendTo.value.trim()
    sendOpen.value = false
    notifySuccess(`Quote sent to ${saved.sentTo ?? sendTo.value.trim()}.`)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not send the quote.'))
  }
  finally {
    sending.value = false
  }
}

/**
 * Numerar y dar por entregado, **sin mandar mail ni abrir el papel**.
 *
 * El presupuesto que se entrega en el mostrador o se manda por WhatsApp no pasa
 * por el envío, así que sin esto se quedaba en borrador y sin número para
 * siempre: el papel que el cliente se llevaba no se podía referenciar por
 * teléfono, y el listado seguía diciendo que nadie lo había ofrecido.
 *
 * No abre el PDF: emitir e imprimir son dos decisiones, y encadenarlas manda a
 * una pestaña nueva a quien solo quería el número. El botón del PDF está justo
 * arriba, para el que sí lo quiere.
 */
const issuing = ref(false)

async function issue() {
  const dcId = currentId.value

  if (!dcId)
    return

  issuing.value = true
  try {
    const { quote: saved } = await issueQuote(dcId, quoteId)

    quote.value = saved
    notifySuccess(`Quote ${saved.number ?? ''} generated.`)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not generate the quote.'))
  }
  finally {
    issuing.value = false
  }
}

// ---- La respuesta del cliente ----

const deciding = ref(false)

async function decide(decision: 'approved' | 'rejected') {
  const dcId = currentId.value

  if (!dcId || !quote.value)
    return

  deciding.value = true
  try {
    const { quote: saved } = await decideQuote(dcId, quote.value.id, decision)

    quote.value = saved
    notifySuccess(decision === 'approved' ? 'Marked as approved.' : 'Marked as rejected.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not update the quote.'))
  }
  finally {
    deciding.value = false
  }
}

// ---- Conversión ----

const convertOpen = ref(false)
const convertLoading = ref(false)
const converting = ref(false)
const options = ref<ConversionOptions | null>(null)

/**
 * El rango, tal como lo escribe el picker de la plantilla: `'YYYY-MM-DD'` o
 * `'YYYY-MM-DD to YYYY-MM-DD'`.
 *
 * Se guarda **el string del componente** y se parte recién al buscar. La versión
 * anterior mantenía `from` y `to` por separado y los sincronizaba desde los
 * hooks de flatpickr —`onChange` ignorando las emisiones a medio rango, `onClose`
 * cerrando el día suelto, un `computed` rearmando el string de vuelta— y cada
 * pieza tapaba un caso de la anterior. Con una sola fuente no hay nada que
 * sincronizar: el componente ya sabe hacer día suelto y rango.
 */
const dateRange = ref('')

const RANGE_SEPARATOR = ' to '

/** Los dos extremos. Con un día suelto, los dos son ese día. */
const rangeParts = computed(() => {
  const parts = dateRange.value.split(RANGE_SEPARATOR).map(p => p.trim()).filter(Boolean)

  return { from: parts[0] ?? '', to: parts[1] ?? parts[0] ?? '' }
})

/**
 * Lo elegido: `"índiceDeLínea|idDeSalida"` → plazas de esa salida.
 *
 * Las plazas viven acá y no en el presupuesto porque son de la SALIDA: en un
 * grupo de dos, uno hace diez y el otro cinco, así que cuántos van se decide una
 * por una. Un mapa plano y no un anidado: la clave es lo que el tilde escribe y
 * lo que el envío recorre, y así son la misma cosa.
 */
const picks = reactive<Record<string, number>>({})

const pickKey = (index: number, eventId: string) => `${index}|${eventId}`

const isPicked = (index: number, eventId: string) => pickKey(index, eventId) in picks

const seatsOf = (index: number, eventId: string) => picks[pickKey(index, eventId)] ?? 1

/** Una plaza por defecto: es el caso corriente, y se sube donde haga falta. */
function togglePick(index: number, eventId: string, on: boolean) {
  const key = pickKey(index, eventId)

  if (on)
    picks[key] = picks[key] ?? 1
  else delete picks[key]
}

function setSeats(index: number, eventId: string, value: unknown) {
  const n = Math.max(1, Math.round(Number(value) || 1))

  picks[pickKey(index, eventId)] = n
}

/**
 * Las plazas elegidas de una línea.
 *
 * Se suman las plazas y no las filas: cinco días tildados de a dos son diez
 * salidas contra las quince cotizadas, y contar filas diría cinco.
 */
const pickedSeatsOf = (index: number) =>
  Object.entries(picks)
    .filter(([k]) => k.startsWith(`${index}|`))
    .reduce((n, [, seats]) => n + seats, 0)

/** Lo elegido más lo ya reservado, que es lo que se compara con lo cotizado. */
const totalSeatsOf = (line: ConversionLine) => line.bookedSeats + pickedSeatsOf(line.index)

/** Cuánto se pasa de lo cotizado. Cero cuando no se pasa. */
const overQuoteOf = (line: ConversionLine) => Math.max(0, totalSeatsOf(line) - line.slots)

/**
 * Los alquileres que van en esta pasada: solo los índices.
 *
 * Un tilde y no un desplegable: un alquiler va con **todas** las reservas que se
 * creen acá (§54). Elegir a cuál colgarlo era un desplegable por renglón para
 * responder casi siempre lo mismo, y con quince buceos "a cuál" ya no es ni la
 * pregunta correcta.
 */
const pickedRentals = ref<number[]>([])

const dateConfig = {
  mode: 'range' as const,
  showMonths: 2,
  dateFormat: 'Y-m-d',

  // El pasado no se reserva: una salida de ayer no admite a nadie.
  minDate: 'today',
}

/**
 * El rango arranca en el período del presupuesto, y se puede cambiar.
 *
 * Es una propuesta, no un candado: las fechas de un viaje se mueven, y el panel
 * se vuelve a consultar cuantas veces haga falta. Sin período —que es la mitad de
 * los casos— arranca hoy y una semana: hay que empezar por algún lado.
 */
function seedRange() {
  const iso = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0')

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }

  const from = quote.value?.tripFrom ?? iso(new Date())
  const to = quote.value?.tripTo ?? iso(new Date(Date.now() + 7 * 86400000))

  dateRange.value = from === to ? from : `${from}${RANGE_SEPARATOR}${to}`
}

const pendingServices = computed(() =>

  // Contra las PLAZAS, no contra las reservas: ocho reservas de dos plazas
  // cubren una línea de quince salidas, y contarlas diría ocho (§54.2).
  (options.value?.services ?? []).filter(l => l.bookedSeats < l.slots),
)

/**
 * Los alquileres se siguen ofreciendo aunque ya hayan salido en una conversión
 * anterior.
 *
 * Un alquiler queda "convertido" con su primera reserva, pero convertir es
 * parcial: cinco buceos hoy y diez la semana que viene. Filtrarlo por eso dejaba
 * el equipo puesto solo en los cinco primeros, y sin forma de agregarlo a los
 * otros diez desde acá. Lo que decide si hay algo que ofrecer son los servicios.
 */
const pendingRentals = computed(() =>
  pendingServices.value.length ? (options.value?.rentals ?? []) : [],
)

/** Cuántas plazas faltan para llegar a lo cotizado. Es una cuenta, no un tope. */
const slotsLeft = (line: ConversionLine) => Math.max(0, line.slots - line.bookedSeats)

async function openConvert() {
  seedRange()
  convertOpen.value = true
  await loadOptions()
}

async function loadOptions() {
  const dcId = currentId.value

  const { from, to } = rangeParts.value

  if (!dcId || !from)
    return

  convertLoading.value = true
  try {
    options.value = await getConversionOptions(dcId, quoteId, { from, to })

    // Se limpia lo elegido: una salida del rango anterior ya no está en pantalla
    // y confirmarla a ciegas sería anotar a alguien en un día que nadie miró.
    for (const k of Object.keys(picks)) delete picks[k]

    /*
     * Los alquileres vienen tildados. El equipo acompaña al viaje entero: que el
     * caso normal cueste un gesto y el raro —destildarlo— cueste otro es al revés
     * de como se usa.
     */
    pickedRentals.value = pendingRentals.value.map(l => l.index)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not load the activities for that range.'))
    options.value = null
  }
  finally {
    convertLoading.value = false
  }
}

/**
 * Lo que esta línea ya reservó, para no ofrecerlo de nuevo.
 *
 * **Sin tope** que las escondiera —la cantidad del presupuesto es una estimación
 * de mostrador, y al mirar el calendario quince salidas terminan siendo
 * dieciocho— esto es lo único que impide anotar dos veces al mismo cliente en la
 * misma salida.
 */
const isBooked = (line: ConversionLine, eventId: string) =>
  (line.bookedEventIds ?? []).includes(eventId)

const selectedCount = computed(() => Object.keys(picks).length)

async function submitConvert() {
  const dcId = currentId.value

  if (!dcId || !selectedCount.value)
    return

  converting.value = true
  try {
    const res = await convertQuote(dcId, quoteId, {
      // Una entrada por salida tildada, con las plazas de esa salida.
      services: Object.entries(picks).map(([key, seats]) => {
        const [index, eventId] = key.split('|')

        return { index: Number(index), eventId, seats }
      }),

      // Solo índices: cada alquiler tildado va con TODAS las reservas de la pasada.
      rentals: pickedRentals.value,
    })

    quote.value = res.quote

    // Se relee para traer las salidas de las reservas recién creadas.
    await load()

    /*
     * Nunca es todo o nada (§47.4): se dice qué entró y qué no. Un aviso de
     * éxito a secas escondería la nocturna que quedó afuera por cupo.
     */
    if (res.failed) {
      notifyWarning(
        `${res.created} booked, ${res.failed} could not be: ${
          res.lines.filter(l => l.error).map(l => `${l.label} — ${l.error}`).join('; ')}`,
      )
    }
    else {
      notifySuccess(`${res.created} booking${res.created === 1 ? '' : 's'} created.`)
    }

    // Se recarga el panel: lo convertido ya no se ofrece, y lo que falló sigue
    // ahí para reintentarlo con otra salida.
    await loadOptions()

    if (!res.failed)
      convertOpen.value = false
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not convert the quote.'))
  }
  finally {
    converting.value = false
  }
}

const BLOCKED_TEXT: Record<string, string> = {
  beyond_horizon:
    'That date is further ahead than the calendar goes. The activity will exist closer to the date.',
  no_activity: 'No activity runs on those dates. Create one, or pick another range.',
  not_convertible: 'This is a free-text line, so there is nothing to book.',
}

const qtyLabel = (line: { quantity: number }) => `×${line.quantity}`

function lineKind(line: QuoteLine): { label: string; color: string; icon: string } {
  if (line.kind === 'rental')
    return { label: 'Rental', color: 'info', icon: 'ri-price-tag-3-line' }

  if (line.kind === 'activity')
    return { label: 'Service', color: 'primary', icon: 'ri-calendar-2-line' }

  return { label: 'Manual', color: 'secondary', icon: 'ri-pencil-line' }
}

/** Cuántas líneas convertibles quedan sin todas sus reservas. */
const pendingCount = computed(() =>
  (quote.value?.lines ?? []).filter(l => {
    if (!l.offeringId || l.kind === 'other')
      return false

    // Un alquiler no genera reserva propia: le alcanza con una plaza, la del
    // servicio al que se adjuntó.
    const slots = l.kind === 'activity' ? Math.max(1, l.quantity) : 1

    // Las PLAZAS, no las reservas: desde que se eligen salida por salida son
    // cosas distintas (§54.2). Sin el campo, una reserva era una plaza.
    return (l.bookedSeats ?? l.bookingIds?.length ?? 0) < slots
  }).length,
)

const hasBookings = computed(() => (quote.value?.lines ?? []).some(l => l.bookingIds?.length))
</script>

<template>
  <div>
    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
    />

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VRow v-if="quote">
      <VCol
        cols="12"
        md="9"
      >
        <VCard>
          <VCardText class="pa-sm-8">
            <div class="bt-doc-head d-flex flex-wrap justify-space-between ga-6 pa-6 rounded mb-8">
              <div>
                <div class="d-flex align-center ga-3 mb-3">
                  <VAvatar
                    v-if="diveCenter?.logoUrl"
                    size="44"
                    rounded
                    :image="diveCenter.logoUrl"
                  />
                  <div class="text-h5 font-weight-bold">
                    {{ diveCenter?.name ?? '—' }}
                  </div>
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  <div
                    v-for="(l, i) in issuerAddress"
                    :key="i"
                  >
                    {{ l }}
                  </div>
                  <div v-if="issuerContact.length">
                    {{ issuerContact.join(' · ') }}
                  </div>
                </div>
              </div>

              <div class="text-md-end">
                <div class="text-h6 mb-3">
                  {{ quote.number ? `Quote #${quote.number}` : 'Quote' }}
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  <div>Date issued: {{ formatDate(quote.createdAt) }}</div>
                  <div v-if="quote.validUntil">
                    Valid until: {{ formatDate(quote.validUntil) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Rótulo y dato en la misma línea, como el recibo (§46.4). -->
            <div class="d-flex flex-wrap justify-space-between ga-6 mb-6">
              <div class="d-flex align-center ga-2">
                <h6 class="text-h6">
                  Quote for:
                </h6>
                <!--
                  A la ficha, abierta en Activities: la pregunta que sigue a
                  ver un presupuesto es qué más hizo esa persona (§38.3.1).
                  Sin ficha queda el nombre congelado y nada que abrir.
                -->
                <RouterLink
                  v-if="quote.clientId"
                  :to="`/clients/${quote.clientId}`"
                  class="text-body-2"
                >
                  {{ quote.clientName }}
                </RouterLink>
                <span
                  v-else
                  class="text-body-2 text-medium-emphasis"
                >{{ quote.clientName }}</span>
              </div>

              <!--
                El período y el grupo van con el cliente y no arriba a la derecha:
                son de lo que se cotiza, no del documento — al lado de la fecha de
                emisión se leerían como dos fechas del papel. Igual que en el PDF.
              -->
              <div
                v-if="quote.tripFrom && quote.tripTo"
                class="d-flex align-center ga-2"
              >
                <h6 class="text-h6">
                  Trip
                </h6>
                <span class="text-body-2 text-medium-emphasis">
                  {{ formatDate(quote.tripFrom) }} – {{ formatDate(quote.tripTo) }}
                  <template v-if="quote.tripDays">
                    ({{ quote.tripDays }} {{ quote.tripDays === 1 ? 'day' : 'days' }})
                  </template>
                </span>
              </div>

              <div class="d-flex align-center ga-2">
                <h6 class="text-h6">
                  Divers
                </h6>
                <span class="text-body-2 text-medium-emphasis">{{ quote.divers }}</span>
              </div>

              <div class="d-flex align-center ga-2">
                <h6 class="text-h6">
                  Status
                </h6>
                <VChip
                  :color="STATUS_CHIP[quote.displayStatus].color"
                  size="small"
                >
                  {{ STATUS_CHIP[quote.displayStatus].label }}
                </VChip>
              </div>
            </div>

            <VTable class="border rounded text-no-wrap mb-6">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="text-end">
                    Unit price
                  </th>
                  <th class="text-end">
                    Qty
                  </th>
                  <th class="text-end">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(line, i) in quote.lines"
                  :key="i"
                >
                  <td>
                    <div class="d-flex align-center">
                      <VAvatar
                        size="32"
                        :color="lineKind(line).color"
                        variant="tonal"
                        rounded
                      >
                        <VIcon
                          :icon="lineKind(line).icon"
                          size="18"
                        />
                      </VAvatar>
                      <div class="d-flex flex-column ms-3">
                        <span class="d-block font-weight-medium text-high-emphasis text-truncate">
                          {{ line.label }}
                        </span>
                      </div>
                      <VTooltip
                        v-if="line.bookingIds?.length"
                        :text="line.bookingIds.length > 1
                          ? `Booked · ${line.bookingIds.length} activities`
                          : 'Booked'"
                        location="top"
                      >
                        <template #activator="{ props: tip }">
                          <VIcon
                            v-bind="tip"
                            icon="ri-calendar-check-line"
                            color="success"
                            size="18"
                            class="ms-3"
                          />
                        </template>
                      </VTooltip>
                    </div>
                  </td>
                  <td class="text-end">
                    {{ money(line.unitAmount) }}
                  </td>
                  <td class="text-end">
                    {{ line.quantity }}
                  </td>
                  <td class="text-end font-weight-medium">
                    {{ money(line.amount ?? line.unitAmount * line.quantity) }}
                  </td>
                </tr>
                <tr v-if="!quote.lines.length">
                  <td
                    colspan="4"
                    class="text-center text-medium-emphasis py-6"
                  >
                    No lines.
                  </td>
                </tr>
              </tbody>
            </VTable>

            <div class="d-flex flex-wrap justify-space-between ga-6">
              <div
                class="text-body-2 text-medium-emphasis"
                style="max-inline-size: 60ch; white-space: pre-line"
              >
                {{ quote.notes }}
              </div>

              <div
                class="text-end"
                style="min-inline-size: 220px"
              >
                <template v-if="quote.discount">
                  <div class="d-flex justify-space-between text-body-2 text-medium-emphasis">
                    <span>Subtotal</span>
                    <span>{{ money(quote.subtotal) }}</span>
                  </div>
                  <div class="d-flex justify-space-between text-body-2 text-medium-emphasis">
                    <span>Discount</span>
                    <span>-{{ money(quote.discount) }}</span>
                  </div>
                  <VDivider class="my-2" />
                </template>

                <div class="d-flex justify-space-between align-baseline">
                  <span class="text-caption text-medium-emphasis">Total</span>
                  <span class="text-h4">{{ money(quote.total) }}</span>
                </div>
              </div>
            </div>

            <VDivider class="my-6" />

            <!--
              Lo que sí hay que decir en un presupuesto: que no cobró nada y
              que el precio puede moverse (§47.1).
            -->
            <VAlert
              variant="outlined"
              density="compact"
              class="text-body-2"
            >
              <strong>This is a quote, not an invoice.</strong> Availability is confirmed when the
              activities are booked, and prices may change after the date shown above. Nothing has
              been charged.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex flex-column ga-3">
            <VBtn
              block
              prepend-icon="ri-file-pdf-2-line"
              :loading="pdfLoading"
              @click="openPdf"
            >
              Quote PDF
            </VBtn>

            <!-- Un rechazado no se reenvía: se hace uno nuevo. -->
            <VBtn
              v-if="quote.status !== 'rejected'"
              block
              variant="outlined"
              prepend-icon="ri-mail-send-line"
              @click="openSend"
            >
              {{ quote.sentAt ? 'Send again' : 'Send by email' }}
            </VBtn>

            <!--
              Para el que se imprime en el mostrador o se manda por otro canal:
              numera y lo da por entregado, y abre el papel. Sin esto quedaba en
              borrador y sin número para siempre.
            -->
            <VBtn
              v-if="quote.status === 'draft'"
              block
              variant="outlined"
              prepend-icon="ri-hashtag"
              :loading="issuing"
              @click="issue"
            >
              Generate
            </VBtn>

            <!--
              Con `sentTo` salió por mail; sin él, en mano. Son dos cosas
              distintas y el papel de una no dice lo de la otra.
            -->
            <div
              v-if="quote.sentAt"
              class="text-caption text-medium-emphasis"
            >
              <template v-if="quote.sentTo">
                Sent to {{ quote.sentTo }} on {{ formatDate(quote.sentAt) }}
              </template>
              <template v-else>
                Issued on {{ formatDate(quote.sentAt) }}
              </template>
            </div>

            <VDivider />

            <!--
              La respuesta del cliente la carga el mostrador: no hay portal
              público de presupuestos, así que esto registra una conversación que
              pasó por teléfono o por mail.
            -->
            <template v-if="quote.status === 'sent'">
              <div class="text-caption text-medium-emphasis">
                What did the client say?
              </div>
              <div class="d-flex ga-2">
                <VBtn
                  color="success"
                  :loading="deciding"
                  @click="decide('approved')"
                >
                  Approved
                </VBtn>
                <VBtn
                  variant="outlined"
                  color="error"
                  :loading="deciding"
                  @click="decide('rejected')"
                >
                  Rejected
                </VBtn>
              </div>
              <VDivider />
            </template>

            <!--
              Convertir es del centro, y por eso abre un panel en vez de hacerlo
              solo (§47.4). Queda disponible mientras falte algo por convertir:
              se hacen dos días hoy y el resto cuando aparezcan las salidas.
            -->
            <VBtn
              v-if="quote.status === 'approved' && pendingCount"
              block
              color="primary"
              prepend-icon="ri-calendar-check-line"
              @click="openConvert"
            >
              Convert to bookings
            </VBtn>

            <!--
              Con reservas creadas, el recibo las levanta solo: es el camino de
              §46, que tarifa con la lista de hoy. Así se cobra ahora o cuando el
              buzo llegue, y en los dos casos con el precio de ese momento.
            -->
            <VBtn
              v-if="hasBookings && quote.clientId"
              block
              variant="outlined"
              prepend-icon="ri-receipt-line"
              :to="`/sales/receipts/new?clientId=${quote.clientId}`"
            >
              Create receipt
            </VBtn>

            <VBtn
              v-if="quote.status === 'draft'"
              block
              variant="outlined"
              prepend-icon="ri-pencil-line"
              :to="`/sales/quotes/${quote.id}`"
            >
              Edit
            </VBtn>

            <VBtn
              block
              variant="text"
              to="/sales/quotes"
            >
              All quotes
            </VBtn>
          </VCardText>
        </VCard>

        <!--
          Contra qué quedó reservado. El ícono de la línea dice QUE se convirtió;
          esto dice CONTRA QUÉ, que es otra pregunta — la salida elegida bien
          puede ser otro día del que se pidió, y con el ícono solo no hay forma
          de saberlo.
        -->
        <VCard
          v-if="bookings.length"
          class="mt-6"
        >
          <VCardItem>
            <VCardTitle class="text-body-1">
              Booked activities
            </VCardTitle>
          </VCardItem>

          <VDivider />

          <!--
            Con el color de su servicio, igual que las actividades de la ficha del
            cliente: el color codifica QUÉ sale (§20.8), así que una semana de
            fondos iguales no distinguiría un curso de una salida en barco. Es el
            mismo `VAlert` con borde al inicio que usa aquella lista, para que las
            dos se lean igual.
          -->
          <VCardText>
            <RouterLink
              v-for="b in bookings"
              :key="b.bookingId"
              :to="`/dive-centers/${currentId}/calendar?date=${b.date}&event=${b.eventId}`"
              class="d-block text-decoration-none"
            >
              <VAlert
                border="start"
                variant="tonal"
                density="compact"
                :color="serviceColor(b.taxonomyCode)"
                :border-color="serviceColor(b.taxonomyCode)"
                class="mb-2"
              >
                <div class="d-flex align-center ga-3">
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium">
                      {{ b.time ? `${b.time} ` : '' }}{{ b.title }}
                    </div>
                    <div class="text-caption">
                      {{ formatDate(b.date) }}
                      <span v-if="b.seats > 1"> · {{ b.seats }} seats</span>
                      <!--
                        De qué renglones salió: un servicio y los alquileres
                        que se le adjuntaron entran como una sola reserva.
                      -->
                      <template v-if="b.lines.length > 1">
                        · {{ b.lines.join(' · ') }}
                      </template>
                    </div>
                  </div>
                  <VIcon
                    icon="ri-arrow-right-line"
                    size="18"
                  />
                </div>
              </VAlert>
            </RouterLink>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Envío. En la raíz de la página (§24.8). -->
    <VNavigationDrawer
      v-model="sendOpen"
      temporary
      location="end"
      :width="$vuetify.display.smAndDown ? undefined : 460"
      class="bt-drawer"
    >
      <div class="d-flex align-center justify-space-between pa-4">
        <div class="text-h6">
          Send quote
        </div>
        <VBtn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="sendOpen = false"
        />
      </div>

      <VDivider />

      <VForm
        ref="sendFormRef"
        class="pa-4 d-flex flex-column ga-4"
        @submit.prevent="submitSend"
      >
        <!-- Sin "From": el correo sale de Blue Trails (§48.3). -->
        <VTextField
          v-model="sendTo"
          label="To"
          type="email"
          :rules="[emailRule]"
          hint="Saved to the client's record if they didn't have one."
          persistent-hint
        />

        <VTextField
          v-model="sendSubject"
          label="Subject"
          :rules="[requiredRule]"
        />

        <VTextarea
          v-model="sendMessage"
          label="Message"
          rows="8"
          :rules="[requiredRule]"
        />

        <div>
          <VChip
            color="success"
            variant="tonal"
            prepend-icon="ri-attachment-2"
          >
            Quote attached
          </VChip>
        </div>

        <div class="d-flex ga-3">
          <VBtn
            type="submit"
            :loading="sending"
          >
            Send
          </VBtn>
          <VBtn
            variant="outlined"
            color="secondary"
            :disabled="sending"
            @click="sendOpen = false"
          >
            Cancel
          </VBtn>
        </div>
      </VForm>
    </VNavigationDrawer>

    <!--
      Conversión. Más ancho que el resto de los paneles: cada fila lleva la fecha,
      el horario, el título de la salida, sus plazas libres y un campo de asientos,
      y en 560 px eso se parte en dos renglones por salida.
    -->
    <VNavigationDrawer
      v-model="convertOpen"
      temporary
      location="end"
      :width="$vuetify.display.smAndDown ? undefined : 760"
      class="bt-drawer"
    >
      <div class="d-flex align-center justify-space-between pa-4">
        <div>
          <div class="text-h6">
            Convert to bookings
          </div>
          <!--
            Las plazas se dicen ANTES de crear nada: son las que decide el grupo
            del presupuesto, y es lo que determina si la salida tiene lugar.
          -->
          <div class="text-caption text-medium-emphasis">
            Tick the days you are booking, and set how many seats each one takes.
            You can come back for the rest.
            <template v-if="options && options.divers > 1">
              The quote is for {{ options.divers }} divers.
            </template>
          </div>
        </div>
        <VBtn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="convertOpen = false"
        />
      </div>

      <VDivider />

      <!--
        🪤 En una grilla y no en un `d-flex`: el input de flatpickr es
        `position: absolute` y no tiene ancho propio, así que como flex item se
        encoge hasta quedar en un cuadradito vacío (la misma trampa de Reports).
      -->
      <VRow
        align="center"
        class="pa-4"
      >
        <VCol
          cols="12"
          sm="8"
        >
          <AppDateTimePicker
            v-model="dateRange"
            label="Dates"
            class="bt-label-top"
            density="compact"
            :config="dateConfig"
          />
        </VCol>
        <VCol
          cols="12"
          sm="4"
        >
          <VBtn
            block
            variant="tonal"
            :loading="convertLoading"
            @click="loadOptions"
          >
            Search
          </VBtn>
        </VCol>
      </VRow>

      <VDivider />

      <div class="pa-4">
        <VProgressLinear
          v-if="convertLoading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <template v-if="options && !convertLoading">
          <div
            v-if="!pendingServices.length"
            class="text-body-2 text-medium-emphasis"
          >
            Nothing left to convert.
          </div>

          <!-- Los servicios: cada uno necesita una salida. -->
          <div
            v-for="line in pendingServices"
            :key="line.index"
            class="mb-5"
          >
            <div class="font-weight-medium mb-1">
              {{ line.label }}
              <span class="text-caption text-medium-emphasis">{{ qtyLabel(line) }}</span>
            </div>
            <div
              v-if="line.bookedSeats"
              class="text-caption text-success mb-2"
            >
              {{ line.bookedSeats }} of {{ line.slots }} already booked
            </div>

            <!--
              Los días y horarios de ESA actividad, uno por fila y con su propio
              campo de asientos.

              Era un desplegable múltiple, y escondía justo lo que hay que decidir:
              cuántos van a cada salida. En un grupo de dos, uno puede hacer diez
              y el otro cinco, así que el número es de la fila y no del documento.
            -->
            <template v-if="line.candidates.length">
              <!--
                El rótulo de la columna, una vez: en un campo de 72 px no entra
                una etiqueta flotante, y un cuadradito mudo al lado de una fecha
                no dice qué se escribe ahí.
              -->
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption text-medium-emphasis">Activities in range</span>
                <span class="text-caption text-medium-emphasis bt-seats-head">Seats</span>
              </div>

              <!--
                Con el color de su servicio, el mismo `VAlert` de borde al inicio
                que las actividades reservadas de acá abajo y las de la ficha del
                cliente. El color codifica QUÉ sale (§20.8): una lista de veinte
                salidas con el mismo fondo no distingue una nocturna de un curso, y
                acá se está eligiendo justamente entre ellas.
              -->
              <VAlert
                v-for="c in line.candidates"
                :key="c.eventId"
                border="start"
                variant="tonal"
                density="compact"
                :color="serviceColor(c.taxonomyCode)"
                :border-color="serviceColor(c.taxonomyCode)"
                class="mb-2 bt-pick"
                :class="{ 'bt-pick--booked': isBooked(line, c.eventId) }"
                @click="isBooked(line, c.eventId)
                  || togglePick(line.index, c.eventId, !isPicked(line.index, c.eventId))"
              >
                <div class="d-flex align-center ga-3">
                  <!--
                    🪤 `bt-pick__check`: `v-selection-control` viene con
                    `flex: 1 1 auto`, así que suelto en la fila se comía la mitad
                    del ancho y empujaba la fecha hasta el medio.
                  -->
                  <VCheckboxBtn
                    :model-value="isPicked(line.index, c.eventId)"
                    :disabled="isBooked(line, c.eventId)"
                    color="primary"
                    density="compact"
                    class="bt-pick__check"
                  />

                  <!--
                    El ícono del servicio, con su color: el mismo avatar tonal
                    que los renglones del papel.
                  -->
                  <VAvatar
                    size="30"
                    rounded
                    variant="tonal"
                    :color="serviceColor(c.taxonomyCode)"
                  >
                    <VIcon
                      icon="ri-calendar-2-line"
                      size="16"
                    />
                  </VAvatar>

                  <div class="flex-grow-1">
                    <div class="font-weight-medium">
                      {{ formatDate(c.date) }}
                      <span
                        v-if="c.time"
                        class="ms-1"
                      >{{ c.time }}</span>
                    </div>
                    <div class="text-caption">
                      {{ c.title }}
                      <span v-if="isBooked(line, c.eventId)">· already booked</span>
                      <span v-else-if="c.seatsLeft != null">· {{ c.seatsLeft }} left</span>
                    </div>
                  </div>

                  <!--
                    🪤 `@click.stop`: la fila entera tildea, así que sin esto
                    escribir las plazas destilda lo que se está por reservar.
                  -->
                  <VTextField
                    :model-value="seatsOf(line.index, c.eventId)"
                    type="number"
                    min="1"
                    density="compact"
                    hide-details
                    class="bt-seats"
                    :disabled="!isPicked(line.index, c.eventId)"
                    @click.stop
                    @update:model-value="v => setSeats(line.index, c.eventId, v)"
                  />
                </div>
              </VAlert>

              <!--
                Pasarse está PERMITIDO —la cantidad del presupuesto es una
                estimación de mostrador— pero no puede pasar callado: quien
                convierte tiene que enterarse antes de confirmar, no cuando el
                recibo no cierra.
              -->
              <VAlert
                v-if="overQuoteOf(line)"
                type="warning"
                variant="tonal"
                density="compact"
                class="mt-2 text-body-2"
              >
                {{ totalSeatsOf(line) }} seats against {{ line.slots }} quoted —
                {{ overQuoteOf(line) }} over. It will still be booked; adjust the
                quote or the receipt.
              </VAlert>

              <div
                v-else
                class="text-caption text-medium-emphasis mt-1"
              >
                {{ totalSeatsOf(line) }} of {{ line.slots }} chosen<template v-if="slotsLeft(line)">
                  · {{ slotsLeft(line) }} to go
                </template>
              </div>
            </template>

            <!-- Sin salida hay tres motivos y no se arreglan igual (§47.4). -->
            <VAlert
              v-else
              type="warning"
              variant="tonal"
              density="compact"
              class="text-body-2"
            >
              {{ BLOCKED_TEXT[line.blockedBy ?? 'no_activity'] }}

              <!--
                Y el enlace a donde se arregla. "Creá una actividad" sin decir
                dónde obliga a buscarla en el menú y volver; abre en otra pestaña
                para no perder el panel a medio llenar.
              -->
              <div
                v-if="line.blockedBy !== 'not_convertible' && currentId"
                class="mt-2"
              >
                <a
                  :href="`/dive-centers/${currentId}/offerings/seasons`"
                  target="_blank"
                  rel="noopener"
                >
                  Open Seasons
                  <VIcon
                    icon="ri-external-link-line"
                    size="14"
                  />
                </a>
                <span class="mx-1">·</span>
                <a
                  :href="`/dive-centers/${currentId}/offerings/services`"
                  target="_blank"
                  rel="noopener"
                >
                  Open Services
                  <VIcon
                    icon="ri-external-link-line"
                    size="14"
                  />
                </a>
              </div>
            </VAlert>
          </div>

          <!--
            Los alquileres van DENTRO de una salida: no existen en el calendario,
            cuelgan de la reserva que se cree (§47.4).
          -->
          <template v-if="pendingRentals.length">
            <VDivider class="my-4" />
            <div class="text-subtitle-2 mb-1">
              Rentals
            </div>
            <!--
              Se adjunta a TODAS las reservas de la pasada: quince jornadas
              con equipo son quince jornadas con equipo, y el recibo cobra un
              alquiler por día una vez por jornada (§30.3).
            -->
            <div class="text-caption text-medium-emphasis mb-3">
              Each one goes with every booking created here.
            </div>

            <!--
              Un tilde, no un desplegable (§54). Vienen marcados porque el equipo
              acompaña al viaje entero: que el caso normal cueste un gesto y el
              raro cueste otro es al revés de como se usa.
            -->
            <VCheckbox
              v-for="line in pendingRentals"
              :key="line.index"
              v-model="pickedRentals"
              :value="line.index"
              density="compact"
              hide-details
            >
              <template #label>
                <span>
                  {{ line.label }}
                  <span class="text-caption text-medium-emphasis">{{ qtyLabel(line) }}</span>
                  <span
                    v-if="line.bookedCount"
                    class="text-caption text-success"
                  > · already on {{ line.bookedCount }}</span>
                </span>
              </template>
            </VCheckbox>
          </template>
        </template>
      </div>

      <template #append>
        <VDivider />
        <div class="d-flex align-center ga-2 pa-4">
          <span class="text-caption text-medium-emphasis">
            {{ selectedCount }} selected
          </span>
          <VSpacer />
          <VBtn
            variant="text"
            @click="convertOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            :disabled="!selectedCount"
            :loading="converting"
            @click="submitConvert"
          >
            Create bookings
          </VBtn>
        </div>
      </template>
    </VNavigationDrawer>
  </div>
</template>

<style scoped>
/* 🪤 `.v-navigation-drawer__content` viene con `overflow-y: hidden` (§20.7). */
.bt-drawer :deep(.v-navigation-drawer__content) {
  overflow-y: auto;
}

/*
 * El campo de plazas: chico y sin etiqueta flotante.
 *
 * 🪤 **`flex: 0 0` y no solo el ancho.** `v-input` viene con `flex: 1 1 auto`, así
 * que con el `inline-size` puesto igual crecía hasta ocupar media fila.
 *
 * El rótulo lo pone la cabecera del grupo, alineada con esta misma medida —de ahí
 * que las dos compartan el ancho.
 */
.bt-seats {
  flex: 0 0 64px;
  inline-size: 64px;
}

/* Misma trampa: la casilla no puede crecer, o empuja el texto al centro. */
.bt-pick__check {
  flex: 0 0 auto;
}

.bt-seats :deep(input) {
  text-align: center;
}

.bt-seats-head {
  inline-size: 64px;
  margin-inline-end: 16px;
  text-align: center;
}

/* La fila entera tildea, así que tiene que decirlo. */
.bt-pick {
  cursor: pointer;
}

/*
 * Una ya reservada se muestra y no se elige: sacarla de la lista dejaría un hueco
 * en el rango, y quien mira no sabría si ese día no existe o si ya está tomado.
 */
.bt-pick--booked {
  cursor: default;
  opacity: 0.55;
}
</style>
